import { makeAutoObservable } from 'mobx';
import { hubConnection } from 'aspnet-signalr-reactjs';
import { ProfileUser } from 'core/models/account/profile';
import { LiveChatModel, Message } from 'core/models/live-chat';
import { SubscriptionDTO } from 'core/models/product/subscription.dto';
import { ProfileService } from 'core/services/api/profile';
import { SubscriptionService } from 'core/services/api/subscriptions';
import { AuthUtils } from 'core/stores/auth/auth-utils';
import { SiteConfigStore } from 'core/stores/site-config.store';

export enum LiveChatPages {
  LiveChat,
  EndChat,
  Error,
}

export class LiveChatLegacyStore {
  customerNumber!: string;
  country!: string;
  activePage: Partial<LiveChatPages> = LiveChatPages.LiveChat;
  showEvent: boolean = false;
  isChatOpen: boolean = false;
  isInitLoading: boolean = false;
  isChatLoading: boolean = false;
  connection: any = undefined;
  hub: any = undefined;
  subscription?: SubscriptionDTO;
  messages: Message[] = [];
  events: Message[] = [];
  chatModel?: LiveChatModel;
  user!: ProfileUser;

  get latestEvent() {
    return this.showEvent && this.events?.length ? this.events[0] : undefined;
  }

  get isLoading() {
    return this.isInitLoading || this.isChatLoading;
  }

  constructor() {
    makeAutoObservable(this);
  }

  initialize(customerNumber: string, country: string, shouldOpen = false) {
    this.changeIsInitLoading(true);

    this.changeCountry(country);
    this.changeCustomerNumber(customerNumber);

    Promise.all([
      ProfileService.getProfile(customerNumber, country),
      SubscriptionService.getSubscriptions(customerNumber, country)
    ])
      .then(([profileUser, subscriptions]) => {
        this.changeSubscription(
          subscriptions.subscriptions.find(
            (sub) => sub.isPrimarySc
          )
        );
        this.changeUser(profileUser);

        if (shouldOpen) {
          this.startChat();
          this.changeIsChatOpen(true);
        }
        this.changeIsInitLoading(false);
      })
      .catch((error) => {
        this.changeIsInitLoading(false);
        this.changeActivePage(LiveChatPages.Error);
        console.error('Failed to retrieve user profile.', error);
      })
  }

  startChat() {
    this.changeIsChatLoading(true);
    try {
      const signalrConnection = hubConnection();
      const hub = signalrConnection.createHubProxy('ChatHub');

      this.registerEvents(hub);
      this.changeConnection(signalrConnection);

      signalrConnection
        .start()
        .done(() => {
          const data = {
            Channel:  SiteConfigStore.channel(),
            BusinessUnit: SiteConfigStore.businessUnit(),
            Country: this.country,
            SmartcardNumber: this.subscription?.smartcardNumber,
            CustomerNumber: this.customerNumber,
            DisplayName: `${this.user.firstName} ${this.user.surname}`,
            FirstName: this.user.firstName,
            LastName: this.user.surname,
            ChatProduct: SiteConfigStore.businessUnit(),
            AuthToken: AuthUtils.GetStoredToken()
          };

          hub
            .invoke('InitiateChatAsync', data)
            .fail((error: any) => {
              console.error(error);
            });

          this.changeHub(hub);
        })
        .fail((error: any) => {
          this.changeIsChatLoading(false);
          this.changeActivePage(LiveChatPages.Error);
          console.error(error);
        });
    } catch (e) {
      this.changeIsChatLoading(false);
      this.changeActivePage(LiveChatPages.Error);
    }
  }

  registerEvents(hub: any) {
    hub.on('initiateChat', (message: string) => {
      this.changeIsChatLoading(false);
      this.changeIsInitLoading(false);
      if (message) {
        this.changeChatModel(JSON.parse(message));
        this.changeActivePage(LiveChatPages.LiveChat);
      } else {
        this.changeActivePage(LiveChatPages.Error);
      }
    });

    hub.on('ReceiveMessage', (messageResponse: string) => {
      if (this.isLoading) {
        this.changeIsInitLoading(false);
        this.changeIsChatLoading(false);
      }
      try {
        const message = JSON.parse(messageResponse) as Message;

        if (message.ConversationStatus === 'Disconnected') {
          this.disconnectChat(false);
        }

        const agentEvents = ['agent has join the chat\n', 'agent has disconnected\n'];
        // NOTE we've been told to exclude senderType null as there are irrelevant messages that come through, 
        //  though there are also relevant one's as per above.
        if (message.SenderType === null) {
          if (agentEvents.some(event => message.Message?.toLowerCase() === event)) {
            this.changeShowEvent(true);
            this.changeEvents([...this.events, message]);
          }
          return;
        }

        // NOTE: from API team - null means its system, human means agent, and customer means customer
        //  'System' and 'Agent' are retained for the legacy API 
        const isIncomingMessage = (
          message.SenderType === 'System' ||
          message.SenderType === 'Agent' ||
          message.SenderType === 'Bot' ||
          message.SenderType === 'Human'
        );

        // NOTE: we've been told not to reference MessageType
        // if (message.MessageType === 'Event') {
        //   this.changeShowEvent(true);
        //   this.changeEvents([...this.events, message]);
        //   return;
        // }

        this.changeShowEvent(false);
        this.changeMessages([...this.messages, {
          isIncomingMessage: isIncomingMessage,
          ...message
        }]);

      } catch (e: any) {
        console.error('Failed to parse message. ', e)
      }
    });

    // NOTE: Can be used for debugging sent messages.
    // hub.on('SendChatToAgent', (messageResponse: string) => {
    //   console.log('Message Sent Event -> ', messageResponse)
    // });
  }

  sendMessage(message: string) {
    try {
      this.hub.invoke('SendChatToAgentAsync', {
        Channel: SiteConfigStore.channel(),
        Country: this.country,
        BusinessUnit: SiteConfigStore.businessUnit(),
        Message: message,
        MessageText: message,
        SmartcardNumber: this.subscription?.smartcardNumber,
        CustomerNumber: this.customerNumber,
        Token: this.chatModel?.token,
        SenderType: 'Customer',
        ...(this.chatModel?.conversationId ? { ConversationId: this.chatModel?.conversationId } : {}),
        ...(this.chatModel?.chatHubConnectionId ? { ChatHubConnectionId: this.chatModel?.chatHubConnectionId } : {}),
        ...(this.chatModel?.websocketId ? { WebsocketId: this.chatModel?.websocketId } : {}),
      });
    } catch (e) {
      console.error('Failed to send message. ', e);
    }
  }

  disconnectChat(closeChat = true) {
    try {

      this.hub.invoke('DisconnectChatAsync', {
        Channel: SiteConfigStore.channel(),
        Country: this.country,
        BusinessUnit: SiteConfigStore.businessUnit(),
        SmartcardNumber: this.subscription?.smartcardNumber,
        CustomerNumber: this.customerNumber,
        Token: this.chatModel?.token,
        ...(this.chatModel?.conversationId ? { ConversationId: this.chatModel?.conversationId } : {}),
        ...(this.chatModel?.chatHubConnectionId ? { ChatHubConnectionId: this.chatModel?.chatHubConnectionId } : {}),
        ...(this.chatModel?.websocketId ? { WebsocketId: this.chatModel?.websocketId } : {}),
      });

      this.connection.stop();

    } catch (e) {
      console.error('Failed to send message. ', e);
    }

    this.changeConnection(undefined);
    this.changeHub(undefined);
    this.changeChatModel(undefined);

    if (closeChat) {
      this.changeMessages([]);
      this.changeIsChatOpen(false);
      this.changeActivePage(LiveChatPages.LiveChat);
    }
  }

  changeCustomerNumber(customerNumber: string) {
    this.customerNumber = customerNumber;
  }

  changeCountry(country: string) {
    this.country = country;
  }

  changeConnection(connection: any) {
    this.connection = connection;
  }

  changeHub(hub: any) {
    this.hub = hub;
  }

  changeMessages(messages: Message[]) {
    this.messages = messages;
  }

  changeEvents(events: Message[]) {
    this.events = events;
  }

  changeChatModel(model: LiveChatModel | undefined) {
    this.chatModel = model;
  }

  changeUser(user: ProfileUser) {
    this.user = user;
  }

  changeActivePage(page: LiveChatPages) {
    this.activePage = page;
  }

  changeSubscription(subscription?: SubscriptionDTO) {
    this.subscription = subscription;
  }

  changeShowEvent(showEvent: boolean) {
    this.showEvent = showEvent;
  }

  changeIsInitLoading(loading: boolean) {
    this.isInitLoading = loading;
  }

  changeIsChatLoading(loading: boolean) {
    this.isChatLoading = loading;
  }

  changeIsChatOpen(open: boolean) {
    this.isChatOpen = open;
  }
}